/**
 * AudioAtmosphere — keeps the game's sound bed alive.
 *
 * Responsibilities:
 *
 * 1. **Multi-epoch ambient symphony.** JB task #92: «каждая эпоха
 *    наследует звуки предыдущих с убывающим весом». Вместо одного
 *    ambient_epoch{N} играющего в момент времени, все предыдущие
 *    эпохи продолжают звучать на убывающей громкости. Эпоха 3:
 *      ambient_epoch1 на 25%, ambient_epoch2 на 55%, ambient_epoch3 на 100%
 *    Звук становится «оркестром эпох» — игрок чувствует глубину
 *    пройденного пути.
 *
 * 2. **Ambient variant cycling.** Каждые 90-180 sec — cross-fade на
 *    другой вариант current epoch'а (manifest содержит 4-6 variants).
 *
 * 3. **Atmospheric breath.** Scheduler из random-events эмитит
 *    `atmospheric_breath_fire` события, плюс idle-tick делает то же
 *    самое если игрок ушёл в тишину.
 *
 * 4. **Watchdog.** 2-секундный таймер проверяет что все слои играют.
 *    Если кто-то выпал (Howler html5 quirks, browser tab throttle) —
 *    тихо рестартует.
 *
 * Wire: mounted once near the top of App.tsx (after UpgradesVisualSync).
 * Renders nothing.
 */
import { useEffect, useRef, useState } from 'react';
import { useGameStore } from '../stores/game.js';
import { useRandomEventsStore } from '../random-events/store.js';
import {
  cycleLoopVariant,
  isLoopPlaying,
  playSound,
  setLoopVolumeMultiplier,
  stopAllLoops,
} from './player.js';
import { useSoundSettings } from './settings.js';
import { SOUND_MANIFEST, type SoundEventId } from './manifest.js';

/** Idle-tick fires roughly every 12–20 sec; randomized to feel organic. */
const IDLE_TICK_MIN_MS = 12_000;
const IDLE_TICK_MAX_MS = 22_000;

/**
 * Watchdog interval — how often to verify the ambient loop is still playing.
 * JB 2026-05-25: ambient «перестаёт играть в какой то момент» — watchdog
 * ловит выпадения и рестартует.
 */
const AMBIENT_WATCHDOG_INTERVAL_MS = 2_000;

/** Ambient cycle — soft variant rotation. */
const AMBIENT_CYCLE_MIN_MS = 90_000;
const AMBIENT_CYCLE_MAX_MS = 180_000;

/** Все ambient loop'ы которыми мы управляем. */
const AMBIENT_EPOCH_IDS = [
  'ambient_epoch1',
  'ambient_epoch2',
  'ambient_epoch3',
] as const satisfies readonly SoundEventId[];

type AmbientId = (typeof AMBIENT_EPOCH_IDS)[number];

/**
 * Multi-epoch symphony layering — какая громкость для каждого слоя
 * на текущей эпохе. Текущая эпоха = 1.0, предыдущие с убывающим
 * весом (геометрический damping: каждая предыдущая в ~0.55 раза тише).
 *
 *   epoch 1: { epoch1: 1.0 }
 *   epoch 2: { epoch1: 0.45, epoch2: 1.0 }
 *   epoch 3: { epoch1: 0.25, epoch2: 0.55, epoch3: 1.0 }
 *
 * При epoch >= 4 (когда контент появится) клипаем нижние слои на 0.15
 * чтобы оркестр не превращался в кашу. Sum громкостей растёт ~линейно
 * с эпохой, но восприятие звука логарифмично — это создаёт «глубину».
 */
function layerWeightsForEpoch(epoch: number): Readonly<Record<AmbientId, number>> {
  const max = Math.min(epoch, AMBIENT_EPOCH_IDS.length);
  const weights: Record<AmbientId, number> = {
    ambient_epoch1: 0,
    ambient_epoch2: 0,
    ambient_epoch3: 0,
  };
  for (let i = 1; i <= max; i++) {
    const id = AMBIENT_EPOCH_IDS[i - 1]!;
    // Damping: i = текущая → 1.0, чем дальше от текущей тем тише.
    const distanceFromCurrent = max - i;
    const damped = Math.pow(0.55, distanceFromCurrent);
    weights[id] = Math.max(0.15, damped); // floor 0.15 — слышимо но фоном
  }
  // Текущая эпоха всегда полная (override floor).
  const currentId = AMBIENT_EPOCH_IDS[max - 1]!;
  weights[currentId] = 1.0;
  return weights;
}

/**
 * Gate всей audio activity (loops + breath + watchdog) за первый user
 * gesture. Browser autoplay policy (Chrome, Safari, Firefox) блокирует
 * Audio.play() до того как user взаимодействовал со страницей. До
 * фикса 2026-05-25 watchdog ретраил ambient_epoch* каждые 2s до первого
 * клика → 286 sound.failed событий в первой production-сессии. После
 * первого pointer / key event ставим unlocked=true и audio стартует.
 *
 * Слушаем pointerdown / touchstart / keydown — все распространённые
 * gesture'ы. `once: true` снимает listener после первого срабатывания.
 */
function useAudioUnlocked(): boolean {
  const [unlocked, setUnlocked] = useState<boolean>(false);

  useEffect(() => {
    if (unlocked) return undefined;
    const unlock = (): void => {
      setUnlocked(true);
    };
    const opts = { once: true, capture: true } as const;
    document.addEventListener('pointerdown', unlock, opts);
    document.addEventListener('touchstart', unlock, opts);
    document.addEventListener('keydown', unlock, opts);
    return () => {
      document.removeEventListener('pointerdown', unlock, opts);
      document.removeEventListener('touchstart', unlock, opts);
      document.removeEventListener('keydown', unlock, opts);
    };
  }, [unlocked]);

  return unlocked;
}

export function AudioAtmosphere(): null {
  const epoch = useGameStore((s) => s.state.epoch);
  const muted = useSoundSettings((s) => s.masterMuted);
  const unlocked = useAudioUnlocked();

  // --- 1. Multi-epoch ambient symphony ---
  // JB task #92. Запускаем все слои [1..epoch] одновременно с весом.
  useEffect(() => {
    // 2026-05-26 BUG FIX: при mute полностью stop'ить ambient loops.
    // Раньше эффект просто возвращал, оставляя running loops с volume 0
    // в фоне (Howler buffers активны, decode CPU, battery). Теперь —
    // stopAllLoops при mute = реальный mute, не visual mute.
    if (muted) {
      stopAllLoops();
      return;
    }
    if (!unlocked) return;
    const weights = layerWeightsForEpoch(epoch);
    const tid = window.setTimeout(() => {
      for (const id of AMBIENT_EPOCH_IDS) {
        const weight = weights[id];
        if (weight > 0) {
          // playSound идемпотентен для loop — стартует только если ещё
          // не играет. Если уже играет — оставляет.
          playSound(id);
          // Корректируем громкость относительно baseVolume на нужный вес.
          setLoopVolumeMultiplier(id, weight);
        } else {
          // Слой не нужен на этой эпохе — приглушаем до 0 (loop технически
          // продолжает крутиться, но беззвучно).
          setLoopVolumeMultiplier(id, 0);
        }
      }
    }, 50);
    return () => {
      window.clearTimeout(tid);
    };
  }, [epoch, muted, unlocked]);

  // --- 2. Atmospheric breath — scheduler-fired ---
  const lastSeenEffectKey = useRef<string | null>(null);
  useEffect(() => {
    if (!unlocked) return undefined;
    const unsub = useRandomEventsStore.subscribe((s) => {
      const newest = s.activeEffects[s.activeEffects.length - 1];
      if (newest === undefined) return;
      const key = `${newest.id}@${newest.emittedAt.toString()}`;
      if (lastSeenEffectKey.current === key) return;
      lastSeenEffectKey.current = key;
      if (newest.id === 'atmospheric_breath_fire' && !muted) {
        playSound('atmospheric_breath');
      }
    });
    return unsub;
  }, [muted, unlocked]);

  // --- 2.1 Atmospheric breath — idle fire ---
  useEffect(() => {
    if (muted || !unlocked) return;
    if (SOUND_MANIFEST.atmospheric_breath.variants.length === 0) return;
    let tid: number | null = null;
    const schedule = (): void => {
      const delay =
        IDLE_TICK_MIN_MS + Math.floor(Math.random() * (IDLE_TICK_MAX_MS - IDLE_TICK_MIN_MS));
      tid = window.setTimeout(() => {
        playSound('atmospheric_breath');
        schedule();
      }, delay);
    };
    schedule();
    return () => {
      if (tid !== null) window.clearTimeout(tid);
    };
  }, [muted, unlocked]);

  // --- 3. Ambient variant cycling per active layer ---
  // Каждый слой ротирует свои варианты независимо. Слои с весом 0
  // пропускаем — нет смысла ротировать беззвучный loop.
  useEffect(() => {
    if (muted || !unlocked) return;
    const weights = layerWeightsForEpoch(epoch);
    const activeIds = AMBIENT_EPOCH_IDS.filter(
      (id) => weights[id] > 0 && SOUND_MANIFEST[id].variants.length >= 2,
    );
    if (activeIds.length === 0) return undefined;

    const timers: number[] = [];
    const scheduleFor = (id: AmbientId): void => {
      const delay =
        AMBIENT_CYCLE_MIN_MS +
        Math.floor(Math.random() * (AMBIENT_CYCLE_MAX_MS - AMBIENT_CYCLE_MIN_MS));
      const tid = window.setTimeout(() => {
        cycleLoopVariant(id);
        scheduleFor(id);
      }, delay);
      timers.push(tid);
    };
    for (const id of activeIds) scheduleFor(id);

    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, [epoch, muted, unlocked]);

  // --- 4. Watchdog для всех активных слоёв ---
  // Стартовый delay 3s даёт audio context'у время прогреться после
  // первого user gesture — иначе watchdog tick'ает сразу после unlock'а,
  // ловит loop ещё в pending-state и пытается restart → autoplay race
  // → sound.failed. Production data (после первого backend wiring)
  // показывал 9 таких race failures за тестовую сессию.
  useEffect(() => {
    if (muted || !unlocked) return;
    let intervalId: number | null = null;
    const startTid = window.setTimeout(() => {
      intervalId = window.setInterval(() => {
        const weights = layerWeightsForEpoch(epoch);
        for (const id of AMBIENT_EPOCH_IDS) {
          if (weights[id] > 0 && !isLoopPlaying(id)) {
            playSound(id);
            setLoopVolumeMultiplier(id, weights[id]);
          }
        }
      }, AMBIENT_WATCHDOG_INTERVAL_MS);
    }, 3_000);
    return () => {
      window.clearTimeout(startTid);
      if (intervalId !== null) window.clearInterval(intervalId);
    };
  }, [epoch, muted, unlocked]);

  return null;
}
